import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { PUBLIC_HOST } from "../lib/domain";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCIPLINE_LABELS = {
  bjj: "Jiu-Jitsu Brésilien",
  muay_thai: "Muay Thaï",
  mma: "MMA",
  kickboxing: "Kickboxing",
  jjk: "Ju-Jitsu Japonais",
  petits_samourais: "Petits Samouraïs",
  boutique: "Boutique",
  merch: "Merchandising",
};

export default function PartenairesPublic() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/b2b/partners/public`);
        setPartners(r.data.partners || []);
      } catch (e) {
        // silent fail
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="partenaires-public">
      {/* HERO */}
      <section className="px-6 sm:px-12 pt-20 pb-12 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">KAIZEN SYSTEM™ · NETWORK</div>
          <h1 className="text-5xl sm:text-7xl font-bold leading-[0.95] mb-6 tracking-tight" data-testid="partenaires-hero">
            Nos partenaires<br />
            <span className="text-amber-400">officiels.</span>
          </h1>
          <p className="text-xl text-zinc-300 max-w-3xl leading-relaxed">
            Les meilleures marques d'arts martiaux au monde — accessibles à nos membres
            de l'Institut Impact Élite Martial (Franchise #001 Kaizen System) à des
            <strong className="text-white"> conditions privilégiées</strong>.
          </p>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="px-6 sm:px-12 py-16 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto">
          {loading && <div className="text-zinc-500 text-sm">Chargement…</div>}
          {!loading && partners.length === 0 && (
            <div className="text-zinc-500 italic">Liste de partenaires en cours d'activation.</div>
          )}
          <div className="space-y-12">
            {partners.map((p) => (
              <PartnerCard key={p.id} partner={p} />
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT EN PROFITER */}
      <section className="px-6 sm:px-12 py-20 bg-zinc-950 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">COMMENT COMMANDER</div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-12 tracking-tight">3 façons simples.</h2>

          <div className="grid md:grid-cols-3 gap-6">
            <PathCard
              num="01"
              title="Tu es membre"
              testid="path-member"
              body={
                <>
                  Parle directement à <strong className="text-amber-300">Kru Ralph</strong> au dojo,
                  ou écris à <a href="mailto:ralph646@hotmail.com" className="text-blue-400 underline">ralph646@hotmail.com</a>.
                  On commande pour toi via le compte Kaizen — tu paies, on s'occupe du reste.
                  <br /><br />
                  <span className="text-zinc-500 text-sm">⏱ Livraison typique : 5-10 jours ouvrés</span>
                </>
              }
            />
            <PathCard
              num="02"
              title="Tu vis à Val-d'Or"
              testid="path-region"
              body={
                <>
                  Pas membre, mais tu veux profiter du tarif Kaizen sur de l'équipement?
                  <strong className="text-amber-300"> Passe au dojo</strong> (1218 7e Rue, Val-d'Or)
                  ou téléphone au <a href="tel:+18195279446" className="text-blue-400 underline">819-527-9446</a>.
                  Cours d'essai 19,99 $ + on commande ton équipement.
                </>
              }
            />
            <PathCard
              num="03"
              title="Tu es ailleurs au Canada"
              testid="path-canada"
              body={
                <>
                  Visite notre boutique en ligne <a href="/store" className="text-blue-400 underline">{PUBLIC_HOST}/store</a> —
                  les produits Kaizen merch sont expédiés directement chez toi via Printful.
                  Pour les marques B2B (Hatashita & co), contacte-nous pour une commande sur mesure.
                </>
              }
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-12 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
            Tu veux essayer un cours <span className="text-amber-400">à 19,99 $</span> ?
          </h2>
          <p className="text-zinc-400 mb-8">
            7 disciplines · 87 élèves actifs · Val-d'Or, Québec
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+18195279446">
              <Button size="lg" className="bg-amber-500 text-black hover:bg-amber-400 font-semibold px-8" data-testid="partenaires-cta-call">
                📞 819-527-9446
              </Button>
            </a>
            <a href="mailto:ralph646@hotmail.com">
              <Button size="lg" variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 px-8" data-testid="partenaires-cta-email">
                ✉️ ralph646@hotmail.com
              </Button>
            </a>
          </div>
        </div>
      </section>

      <footer className="px-6 sm:px-12 py-10 text-xs text-zinc-600 text-center border-t border-zinc-900">
        KAIZEN SYSTEM™ — Institut Impact Élite Martial (Franchise #001) · {PUBLIC_HOST}<br />
        Val-d'Or, Québec, Canada
      </footer>
    </div>
  );
}

const PartnerCard = ({ partner }) => (
  <div
    className="border border-zinc-800 rounded-2xl p-8 bg-gradient-to-br from-zinc-950 to-black hover:border-amber-500/40 transition-all"
    data-testid={`partner-card-${partner.id}`}
  >
    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
      <div>
        <div className="text-amber-400 text-xs tracking-[0.3em] mb-2">PARTENAIRE OFFICIEL</div>
        <h3 className="text-3xl sm:text-4xl font-bold tracking-tight">{partner.brand}</h3>
      </div>
      <div className="flex gap-2 flex-wrap">
        {(partner.disciplines || []).map((d) => (
          <Badge key={d} variant="outline" className="border-amber-500/40 text-amber-300 text-xs">
            {DISCIPLINE_LABELS[d] || d}
          </Badge>
        ))}
      </div>
    </div>

    {partner.promo_blurb && (
      <p className="text-zinc-300 leading-relaxed mb-6 text-lg">{partner.promo_blurb}</p>
    )}

    {partner.promo_benefits?.length > 0 && (
      <div className="bg-black border border-zinc-900 rounded-xl p-6">
        <div className="text-amber-400 text-xs tracking-[0.3em] mb-4">CE QUE ÇA T'APPORTE</div>
        <ul className="space-y-3" data-testid={`partner-${partner.id}-benefits`}>
          {partner.promo_benefits.map((b, i) => (
            <li key={i} className="flex gap-3 text-zinc-300">
              <span className="text-amber-400 shrink-0">✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {partner.portal_url && (
      <a
        href={partner.portal_url}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-zinc-500 hover:text-blue-400 mt-4 inline-block"
      >
        Site officiel : {partner.portal_url.replace(/^https?:\/\//, "")}
      </a>
    )}
  </div>
);

const PathCard = ({ num, title, body, testid }) => (
  <div className="border border-zinc-800 rounded-2xl p-6 bg-black" data-testid={testid}>
    <div className="text-4xl font-bold text-amber-400 mb-3">{num}</div>
    <h3 className="text-xl font-bold mb-3">{title}</h3>
    <div className="text-zinc-300 text-sm leading-relaxed">{body}</div>
  </div>
);
